import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { BaseEntityService } from '../entity-management/services/base/base-entity.service';
import { CrmPersonSearches } from '../models/crm-person-searches';
import { DataWorkerService } from '../services/data-worker.service';
import { RouterParamsService } from '../../shared/services/router-params.service';
import { Person } from '../models/person';


type AnyResult = Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class CrmPersonSearchesEntityService extends BaseEntityService<CrmPersonSearches> {
  storageName = 'lastQuery';
  clearCache = environment.version;
  private currentOrganizationId?: number;
  private lastQuery?: string;

  /**
   * Local reactive store for consolidated results (de-duplicated by key).
   * We keep a dictionary for O(1) upserts and expose an array observable for UI.
   */
  private readonly resultsDict$ = new BehaviorSubject<Record<string, AnyResult>>({});
  readonly results$: Observable<AnyResult[]> = this.resultsDict$.asObservable().pipe(
    map(dict => Object.values(dict))
  );

  constructor(
    private dataWorkerService: DataWorkerService,
    httpClient: HttpClient,
    routerParamsService: RouterParamsService,
  ) {
    super(
      httpClient,
      environment.apiUrl,
      { singular: 'crmPersonSearch', plural: 'crmPersonSearches', url: 'crm-person-searches' },
      routerParamsService
    );
    this.nameId = 'crmPersonSearchId';

    this.dataWorkerService.deleteDatabase();
  }

  /**
   * Public helper to clear the in-memory results store.
   */
  resetResultsStore(): void {
    this.resultsDict$.next({});
  }

  /**
   * Choose the entity key used to upsert/merge items.
   * Priority: "id" -> "person.id" -> nameId -> JSON string fallback.
   */
  private keySelector = (item: AnyResult): string => {
    if (item?.id != null) return String(item.id);
    if (item?.person?.id != null) return String(item.person.id);
    if (this.nameId && item?.[this.nameId] != null) return String(item[this.nameId]);
    // Last-resort fallback to a stable signature (rarely used)
    return JSON.stringify({ id: item?.id ?? null, personId: item?.person?.id ?? null });
  };

  /**
   * Shallow equality (with simple arrays & plain objects support) to avoid unnecessary emissions.
   */
  private shallowEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;

    const isPlainObj = (v: any) => Object.prototype.toString.call(v) === '[object Object]';
    const isArr = Array.isArray;

    // Basic arrays: same length + shallow compare entries
    if (isArr(a) && isArr(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    }

    // Plain objects: same keys + shallow compare values
    if (isPlainObj(a) && isPlainObj(b)) {
      const ak = Object.keys(a);
      const bk = Object.keys(b);
      if (ak.length !== bk.length) return false;
      for (const k of ak) if (a[k] !== b[k]) return false;
      return true;
    }

    return false;
  }

  /**
   * Merge strategy:
   * - If identical (shallow) -> return previous reference to avoid churn.
   * - Else -> return a new object `{...prev, ...incoming}` (shallow merge).
   * You can customize this to deep-merge specific nested fields if needed.
   */
  private mergeIfChanged(prev: AnyResult | undefined, incoming: AnyResult): AnyResult {
    if (!prev) return incoming;
    const merged = { ...prev, ...incoming };
    return this.shallowEqual(prev, merged) ? prev : merged;
  }

  /**
   * Remove items from the in-memory store by an array of IDs.
   * Supports keys stored by `id`, `person.id`, or `this.nameId`.
   */
  private removeByIds(ids: Array<string | number>): void {
    if (!ids?.length) return;
    const current = { ...this.resultsDict$.value };
    let changed = false;
    const targetIds = new Set(ids.map(id => String(id)));

    // 1) Try direct key match (fast path)
    for (const id of targetIds) {
      if (current[id]) {
        delete current[id];
        changed = true;
      }
    }

    // 2) Fallback: scan entries to match id/person.id/nameId
    for (const [key, value] of Object.entries(current)) {
      const candidate =
        (value && (value as any).id != null ? String((value as any).id) : undefined) ??
        (value && (value as any).person?.id != null ? String((value as any).person.id) : undefined) ??
        (this.nameId && value && (value as any)[this.nameId] != null ? String((value as any)[this.nameId]) : undefined);

      if (candidate && targetIds.has(candidate)) {
        delete current[key];
        changed = true;
      }
    }

    if (changed) {
      this.resultsDict$.next(current);
    }
  }

  /**
   * Upsert an array of results into the local store.
   * - De-duplicates by keySelector
   * - Merges when necessary
   * - Emits only when something realmente mudou
   */
  private upsertMany(incoming: AnyResult[]): void {
    if (!incoming?.length) return;

    const current = this.resultsDict$.value;
    let changed = false;
    const nextDict = { ...current };

    for (const item of incoming) {
      const key = this.keySelector(item);
      const updated = this.mergeIfChanged(nextDict[key], item);
      if (updated !== nextDict[key]) {
        nextDict[key] = updated;
        changed = true;
      }
    }
    if (changed) this.resultsDict$.next(nextDict);
  }

  /**
   * Main API call.
   * Agora exige organizationId (number).
   * Sempre que organizationId mudar, limpa o estado (store + lastQuery).
   */
  fetchCrmPersonSearches(fields: string[], organizationId: number, clearCache: boolean = false, clearLastQuery: boolean = false) {
    fields = fields.filter(f => !['person.id'].includes(f));

    const orgChanged = this.currentOrganizationId !== organizationId;
    if (orgChanged) {
      this.currentOrganizationId = organizationId;
      this.resetResultsStore();
      this.lastQuery = undefined;
      this.clearLastQuery();
    }

    if (clearCache) {
      this.resetResultsStore();
      this.lastQuery = undefined;
      this.clearLastQuery();
    }

    if (clearLastQuery) {
      this.lastQuery = undefined;
    }

    const body: Record<string, any> = { fields, organization: { id: organizationId } };
    if (this.lastQuery) {
      body['changesSince'] = this.lastQuery;
    }

    return this.add(body).pipe(
      tap((response: any) => {
        // Persiste marcador de incremental
        this.lastQuery = response?.queryStart;

        if (Array.isArray(response?.deletions) && response.deletions.length) {
          this.removeByIds(response.deletions);
        }

        // Upsert no store local
        const incoming = Array.isArray(response?.results) ? response.results : [];
        this.upsertMany(incoming);
      })
    );
  }


  getCrmPersonSearch(organizationId: number, clearCache = false, clearLastQuery = false) {
    const fields = [
      'id',
      'name',
      'document',
      'contractStartDate',
      'simpleDigitalStatus',
      'hrId',
      'orgGroupId',
      'eligibleForIncentive',
      'ownership',
      'hrName',
    ];
    return this.fetchCrmPersonSearches(fields, organizationId, clearCache, clearLastQuery);
  }

  getCrmPersonSearchConcierge(organizationId: number, fields: string[], clearCache = false, clearLastQuery = false) {
    return this.fetchCrmPersonSearches(fields, organizationId, clearCache, clearLastQuery);
  }

  clearLastQuery(): void {
    localStorage.setItem(this.storageName, '');
  }
}

export interface CrmPersonSearchesPreventionAdherence {
  id: number;
  name: string;
  document: string;
  contractStartDate: string;
  simpleDigitalStatus: string;
  hrId: number;
  orgGroupId: number;
}

export interface CrmPersonSearchesConcierge {
  id: number;
  person: Person;
  name: string;
  birthdate: string;
  hoProduct: string;
  lastReferralRequestedAt: string;
  lastFormSubmissionAt: string;
  successPatientInteractions: number;
  failurePatientInteractions: number;
  referralsOriginatedBy: string[];
  referralsOriginatedByFormat: string;
  document: string;
  phone: string;
  digitalStatus: string;
  pregnantAnswer: boolean;
  hrId: number;
  orgGroupId: number;
  city: string;
  state: string;
  eligibleForIncentive: boolean;
  financialRisk: 'HIGH' | 'LOW';
}
